import React, { useMemo, useRef, useState, useCallback, useEffect } from 'react';
import { MultiSelect } from 'primereact/multiselect';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { useGetChassisDropdownListQuery } from '../../api';
import { useLanguage } from '../../contexts/LanguageContext';

const normalizeText = (str) =>
    (str || '')
        .replace(/\s+/g, ' ')
        .replace(/\u00A0/g, ' ')
        .trim();

const formatSearchText = (text) => (text ? text.replace(/\s+/g, '').toUpperCase() : '');

const ChassisMultiSelectDropdown = ({
    value,
    onChange,
    className = '',
    disabled = false,
    showDescription = true,
    rowCount = 200,
    singleSelection = false
}) => {
    const { getCurrentLanguageId, th } = useLanguage();
    const languageId = getCurrentLanguageId();

    const [dropdownFilterValue, setDropdownFilterValue] = useState('');
    const [searchText, setSearchText] = useState('');
    const debounceRef = useRef(null);

    const handleFilterInputChange = useCallback((val) => {
        setDropdownFilterValue(val);
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => setSearchText((val ?? '').trim()), 450);
    }, []);

    useEffect(() => {
        return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
    }, []);

    const formattedSearchText = useMemo(() => formatSearchText(searchText), [searchText]);

    const { data: chassisList = [], isLoading } = useGetChassisDropdownListQuery({
        RowCount: rowCount,
        SearchText: formattedSearchText,
        languageId
    });

    // ✅ pick() kaldırıldı, normalize useMemo'ya taşındı
    const apiOptions = useMemo(() => {
        return (chassisList || [])
            .map((ch) => {
                const id = String(ch?.id ?? ch?.Id ?? '');
                if (!id) return null;
                return {
                    id,
                    text: id,
                    rawText: normalizeText(ch?.translatedText ?? ch?.TranslatedText ?? ''),
                    licensePlate: String(ch?.licensePlate ?? ch?.LicensePlate ?? ''),
                    fullData: ch
                };
            })
            .filter(Boolean);
    }, [chassisList]);

    // ✅ value prop'undan id listesi türet
    const selectedIds = useMemo(() => {
        return Array.isArray(value)
            ? value.map((v) => String(typeof v === 'object' ? v?.id ?? v?.Id : v)).filter(Boolean)
            : [];
    }, [value]);

    // ✅ Seçili ama listede olmayan option'ları merge et (seçim kaybolmasın)
    const mergedOptions = useMemo(() => {
        const map = new Map(apiOptions.map((o) => [o.id, o]));
        selectedIds.forEach((id) => {
            if (!map.has(id)) {
                map.set(id, { id, text: id, rawText: '', licensePlate: '', fullData: null });
            }
        });
        return Array.from(map.values());
    }, [apiOptions, selectedIds]);

    // ✅ useEffect kaldırıldı, selectedMulti/selectedSingle useMemo ile hesaplanıyor
    const selectedMulti = useMemo(
        () => selectedIds.map((id) => mergedOptions.find((o) => o.id === id)).filter(Boolean),
        [selectedIds, mergedOptions]
    );

    const selectedSingle = useMemo(
        () => selectedIds[0] ? (mergedOptions.find((o) => o.id === selectedIds[0]) ?? null) : null,
        [selectedIds, mergedOptions]
    );

    const emptyMessage = (dropdownFilterValue ?? '').trim() ? 'Kayıt bulunamadı' : 'Kayıt yok';

    const filterTemplate = useCallback(
        () => (
            <div className="p-dropdown-filter-container" onMouseDown={(e) => e.stopPropagation()}>
                <InputText
                    value={dropdownFilterValue}
                    onChange={(e) => handleFilterInputChange(e.target.value)}
                    className="p-dropdown-filter w-full"
                    placeholder={th('common.search')}
                    autoFocus
                    disabled={disabled}
                    onKeyDown={(e) => e.stopPropagation()}
                    onKeyUp={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                />
                <span className="p-dropdown-filter-icon pi pi-search" />
            </div>
        ),
        [dropdownFilterValue, handleFilterInputChange, disabled, th]
    );

    const itemTemplate = (opt) => {
        if (!opt) return null;
        if (!showDescription) return <span className="font-medium">{opt.id}</span>;
        return (
            <div className="flex flex-column">
                <span className="font-medium">{opt.id}</span>
                <small className="text-500">
                    {opt.licensePlate ? `${opt.licensePlate} • ` : ''}
                    {opt.rawText}
                </small>
            </div>
        );
    };

    const selectedItemText = (opt) => {
        if (!opt) return '';
        if (!showDescription) return opt.id;
        return `${opt.id}${opt.rawText ? ` - ${opt.rawText}` : ''}`;
    };

    const clearFilter = () => {
        setDropdownFilterValue('');
        setSearchText('');
    };

    // ✅ setSelectedSingle/setSelectedMulti kaldırıldı
    // value prop → parent günceller → useMemo yeniden hesaplar
    const handleSingleChange = (e) => {
        const opt = e.value ?? null;
        onChange?.(opt?.id ? [opt.id] : [], opt?.fullData ? [opt.fullData] : []);
        clearFilter();
    };

    const handleMultiChange = (e) => {
        const selected = Array.isArray(e.value) ? e.value.filter(Boolean) : [];
        onChange?.(
            selected.map((x) => x?.id).filter(Boolean),
            selected.map((x) => x?.fullData).filter(Boolean)
        );
        clearFilter();
    };

    if (singleSelection) {
        return (
            <Dropdown
                value={selectedSingle}
                options={mergedOptions}
                onChange={handleSingleChange}
                optionLabel="text"
                dataKey="id"
                filter
                filterTemplate={filterTemplate}
                showClear
                className={className}
                placeholder={th('warrantyClaims.detail.form.chassisPlaceholder')}
                loading={isLoading}
                disabled={disabled}
                itemTemplate={itemTemplate}
                valueTemplate={(opt, props) =>
                    opt ? <span>{selectedItemText(opt)}</span> : <span className="text-500">{props.placeholder}</span>
                }
                emptyMessage={emptyMessage}
                appendTo="self"
                virtualScrollerOptions={{ itemSize: 44 }}
            />
        );
    }

    return (
        <MultiSelect
            value={selectedMulti}
            options={mergedOptions}
            onChange={handleMultiChange}
            optionLabel="text"
            dataKey="id"
            filter
            filterTemplate={filterTemplate}
            showClear
            className={className}
            placeholder={th('warrantyClaims.detail.form.chassisPlaceholder')}
            loading={isLoading}
            disabled={disabled}
            itemTemplate={itemTemplate}
            selectedItemTemplate={(opt) => selectedItemText(opt)}
            valueTemplate={(selected, props) => {
                const arr = Array.isArray(selected) ? selected.filter(Boolean) : [];
                if (arr.length === 0) return <span className="text-500">{props.placeholder}</span>;
                if (arr.length === 1) return <span>{selectedItemText(arr[0])}</span>;
                return <span>{arr.length} şasi seçildi</span>;
            }}
            emptyMessage={emptyMessage}
            appendTo="self"
            display="chip"
            virtualScrollerOptions={{ itemSize: 44 }}
        />
    );
};

export default ChassisMultiSelectDropdown;